/**
 * The route guard. No anonymous access to reports — CLAUDE.md §6.7. Base Portal being out of reach
 * is deliberately not a redirect to the login form: nothing is wrong with the user's credential,
 * and a password box implies otherwise.
 */

import { faArrowRotateRight, faPlugCircleXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, retryRestore } = useAuth();
  const location = useLocation();

  if (status === 'restoring') {
    return (
      <div
        role="status"
        className="flex min-h-dvh items-center justify-center gap-3 px-6 text-muted-foreground"
      >
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-base" aria-hidden />
        <p className="text-sm">Checking your sign-in…</p>
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-5 text-center">
          <FontAwesomeIcon
            icon={faPlugCircleXmark}
            className="text-3xl text-muted-foreground"
            aria-hidden
          />
          <div className="space-y-2">
            <h1 className="text-xl font-semibold tracking-tight">
              WinSight can&rsquo;t reach Base Portal
            </h1>
            <p className="text-sm text-muted-foreground">
              Your sign-in can&rsquo;t be checked until it&rsquo;s back, so reports are on hold.
              Nothing is wrong with your account or your password.
            </p>
          </div>
          <Button onClick={retryRestore}>
            <FontAwesomeIcon icon={faArrowRotateRight} aria-hidden />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'anonymous') {
    // Carry where they were headed, filters and all, since those live in the URL (§4.4).
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
